import { Component, Inject, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Arende } from '../../interfaces/arende.interface';
import { ArendeService } from './arende.service';
import { fadeInAnimation } from '../../_animations/fade-in.animation';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorModalService } from '../../modal/error.modal.service';
import { Location } from '@angular/common';
import { HuvudB2 } from '../../interfaces/huvudB2.interface';
import { NgbdB2BilagorModal } from '../../modal/b2-bilagor.modal.component';
import { NgbModalRef, NgbModalOptions, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AttestService } from '../../attest/attest.service';
import { Attest } from '../../interfaces/attest.interface';
import { SessionService } from '../../session/session.service';
import { FiliDb } from '../../interfaces/filiDb.interface';
import { Anstallning } from '../../interfaces/anstallning.interface';

@Component({
    selector: 'app-arenden',
    templateUrl: './arende.component.html',
    styleUrls: ['./arende.component.scss'],
    animations: [
        fadeInAnimation
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ArendeComponent {

  constructor(
    protected service: ArendeService,
    protected spinner: NgxSpinnerService,
    protected sessionService: SessionService,
    protected errorModalService: ErrorModalService,
    protected modalService: NgbModal,
    protected attestService: AttestService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected location: Location,
    @Inject(DOCUMENT) private readonly document: Document) {
    const arendeId = this.route.snapshot.paramMap.get('id');
    this.loadArende(arendeId);
  }

  public get isLoaded(): boolean { return !!this.arende; };
  public get role(): string { return this.sessionService.activeInstance.roleType; };

  public get selectedB2Besluts(): HuvudB2[] { return this.arende.b2Beslut.filter(b => b.isSelected); }
  public get harBeslutKvarAttSignera(): boolean { return !!this.arende.b2Beslut.find(b => b.fil && b.fil.filtillagg.toLowerCase() === '.pdf' && !b.fil.signatur); };

  private lockedWindow: NgbModalRef;

  public userFilter: any;

  public applyFilter() {
    const filterNumber: string = '' + (+this.filterText.replace(/[ .,]/g, ''));

    this.userFilter = {
      $or: [
        {
          arbetstagare: {
            $or: [
              { fornamn: this.filterText },
              { efternamn: this.filterText },
              { personNr12Siffror: this.filterText }
            ]
          }
        },
        { totalLgBelopp: filterNumber },
        { totalFordranBelopp: filterNumber }
      ]
    }
  }

  public activated: boolean = false;

  public anstalldaOrderName: string = 'personNr';
  public anstalldaOrder: string[] = ['arbetstagare.personNr12Siffror'];
  public b2BeslutOrderName: string = 'datum';
  public b2BeslutOrder: string[] = ['datum'];
  public filterText: string;
  public anstalldaReverse: boolean = false;
  public b2BeslutReverse: boolean = true;

  public arende: Arende;

  public get selectedBesluts(): HuvudB2[] { return this.arende.b2Beslut.filter(b => b.isSelected && b.fil); }
  public get selectedFiles(): FiliDb[] { return this.selectedBesluts.map(b => b.fil); }

  private loadArende(id: string) {

    const callback = () => {
      setTimeout(() => {
        if (!this.arende) {
          this.spinner.show();
        }
      }, 300);

      this.service.getArende(this.sessionService.activeInstance.id, id).subscribe(
        (result: Arende) => {
          this.spinner.hide();
          this.arende = result;

          this.sessionService.setSelectedCaseId(this.arende.id);

          this.arende.anstallda.forEach(a => {
            a.isConfidential = a.arbetstagare.fornamn.indexOf('SEKRETESS') !== -1;
          });
        },
        () => {
          this.spinner.hide();
          this.errorModalService.open('Inläsningsfel', 'Kunde inte hämta det begärda ärendet.');
        });
    }

    if (!this.sessionService.hasSession) {
      this.router.navigate(['/login']);
      return;
    }

    if (!this.sessionService.hasActiveInstance) {
      this.sessionService.selectInstance(callback);
      return;
    }

    callback();
  }

  public reloadArende() {
    this.spinner.show();
    this.loadArende(this.arende.id);
  }

  public attesteraBeslut(): void {

    this.spinner.show();

    this.attestService.attestManyArendeBeslut(this.sessionService.activeInstance.id, this.arende.id, this.selectedB2Besluts.map(b => b.id)).subscribe(
      (attest: Attest) => {
        if (attest) {
          this.selectedB2Besluts.map(b2 => {
            b2.attest = attest;
            b2.isSelected = false;
          });
        }
        this.spinner.hide();
      },
      (error) => {
        this.errorModalService.open('Inläsningsfel', 'Kunde inte attestera de valda besluten.');
        this.spinner.hide();
      });
  }

  public showAttachments(b2: HuvudB2): void {
    this.openModal(NgbdB2BilagorModal, { centered: true });
    this.lockedWindow.componentInstance.arende = this.arende;
    this.lockedWindow.componentInstance.b2 = b2;
  }

  private openModal(content, windowClass?: NgbModalOptions): void {
    this.closeModal(this.lockedWindow);
    this.lockedWindow = this.modalService.open(content, windowClass);
  }

  private closeModal(modalRef: NgbModalRef): void {
    if (!!modalRef) {
      modalRef.close();
    }
  }

  public scrollToTable(id: string): void {
    function offset(el): number {
      const rect = el.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      return rect.top + scrollTop;
    }

    this.route.queryParamMap.subscribe(paramMap => {
      const targetElement = this.document.querySelector(`table[id='${id}']`);
      if (targetElement) {
        const top = offset(targetElement) - (window.innerHeight / 5);
        window.scrollTo({
          top: top,
          behavior: 'smooth'
        });
      }
    });
  }

  public goBack(): void {
    this.location.back();
  }

  public get instanceName(): string {
    return this.sessionService.activeInstance.name;
  }

  public setAnstalldaOrder(name: string, order: string[]): void {
    if (this.anstalldaOrderName === name) {
      this.anstalldaReverse = !this.anstalldaReverse;
    }

    this.anstalldaOrderName = name;
    this.anstalldaOrder = order.slice();
  }

  public setB2BeslutOrder(name: string, order: string[]): void {
    if (this.b2BeslutOrderName === name) {
      this.b2BeslutReverse = !this.b2BeslutReverse;
    }

    this.b2BeslutOrderName = name;
    this.b2BeslutOrder = order.slice();
  }

  public getSum(items: number[], property: string): number {
    let sum: number = 0;
    for (let i = 0; i < items.length; i++) {
      sum += items[i][property] as number || 0;
    }

    return sum;
  }

  public visaSomAnstalld(anstalld: Anstallning): void {
    const instance = {
      id: this.sessionService.activeInstance.id,
      name: this.arende.arendenummer || this.arende.galdenar?.namn || 'Ärende',
      roleType: 'hl',
      roleName: 'Handläggare',
      personalNumber: this.sessionService.activeInstance.personalNumber,
      defaultUrl: `/mina-arenden/${this.arende.id}`
    };

    this.sessionService.setInstance(instance, '/beslut', anstalld.arbetstagare.personNr12Siffror, anstalld.arbetstagare.fornamn + ' ' + anstalld.arbetstagare.efternamn);
  }
}
